import { useParams } from '@tanstack/react-router'
import {
  CantileverButton,
  CantileverLinkButton,
} from 'components/CantileverButton'
import { AddLinkModal } from 'features/processFlowDiagrams/components/AddLinkModal'
import { ProcessFlowDiagramBuilderContext } from 'features/processFlowDiagrams/contexts/ProcessFlowDiagramBuilderContext'
import { PfdNode } from 'features/processFlowDiagrams/types'
import { ExternalLink, Plus } from 'lucide-react'
import React, { useContext, useState } from 'react'
import styled from 'styled-components'

const NodeContainer = styled.div`
  position: relative;
  display: inline-block;
`

const FloatingLinkButtonContainer = styled.div`
  position: absolute;
  top: -2rem;
  right: -1rem;
  z-index: 10;
`

export const sharedButtonStyles = `
  width: 2rem;
  height: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.15);

  &:hover {
    opacity: 1;
    transform: scale(1.1);
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const StyledCantileverLinkButton = styled(CantileverLinkButton)`
  ${sharedButtonStyles}
`

const StyledCantileverButton = styled(CantileverButton)`
  ${sharedButtonStyles}
`

export const BaseNodeWrapper = ({
  children,
  link,
  id,
  setNodes,
}: {
  children: React.ReactNode
  id: string
  link?: {
    entryType: string
    entryID: string
    siteID: string
  }
  setNodes: (payload: PfdNode[] | ((nodes: PfdNode[]) => PfdNode[])) => void
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { disableEditing } = useContext(ProcessFlowDiagramBuilderContext)
  const { siteID } = useParams({ strict: false })

  const handleLinkChange = (newLink: {
    entryType: string
    entryID: string
    siteID: string
  }) => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id
          ? { ...node, data: { ...node.data, link: newLink } }
          : node
      )
    )
  }

  const handleModalClick = () => {
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  return (
    <NodeContainer>
      {children}
      {link ? (
        <FloatingLinkButtonContainer>
          <StyledCantileverLinkButton
            to={`/assets/${link.siteID}/${link.entryID}`}
            primary
            iconOnly
            title='Open link'
            aria-label='Open link'>
            <ExternalLink />
          </StyledCantileverLinkButton>
        </FloatingLinkButtonContainer>
      ) : (
        !disableEditing && (
          <FloatingLinkButtonContainer>
            <StyledCantileverButton
              onClick={handleModalClick}
              primary
              iconOnly
              title='Open modal'
              aria-label='Open modal'>
              <Plus />
            </StyledCantileverButton>
          </FloatingLinkButtonContainer>
        )
      )}

      {siteID && (
        <AddLinkModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleLinkChange}
          siteID={siteID}
        />
      )}
    </NodeContainer>
  )
}
